/* organization/OrgAuditLogs — server-side filtering, pagination, detail drawer, RBAC export gate */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill, SearchInput } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, Th } from '../platform/_ui';
import { listByOrg as listOrgAudit } from '../api/audit.api.js';
import { usePermissions } from './auth/usePermissions.js';
import { useOrgRuntime } from './OrganizationRuntimeContext.jsx';
import { DeniedState } from './auth/DeniedState.jsx';
import { CATS, CAT_TONE } from './constants/orgCats.js';

const SEV_TONE  = { info: 'blue', warning: 'amber', critical: 'red' };
const SEVS      = ['All', 'info', 'warning', 'critical'];
const PAGE_SIZE = 50;

const fmtSev = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Info';

const fmtTs = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return String(iso); }
};

function DiffBlock({ before, after }) {
  if (!before && !after) return null;
  return (
    <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
      {before && (
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 2 }}>BEFORE</div>
          <pre style={{ margin: 0, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: 'var(--nx-danger)' }}>
            {JSON.stringify(before, null, 2)}
          </pre>
        </div>
      )}
      {after && (
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 2 }}>AFTER</div>
          <pre style={{ margin: 0, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: 'var(--nx-success)' }}>
            {JSON.stringify(after, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

export default function OrgAuditLogs({ data }) {
  const { orgId }  = useParams();
  const navigate   = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo    = searchParams.get('returnTo');
  const returnLabel = searchParams.get('returnLabel');
  const toast      = useToast();
  const perm       = usePermissions();
  const { loading: runtimeLoading } = useOrgRuntime();
  const org        = data.state.organizations.find(o => o.id === orgId);

  const [logs, setLogs]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [q, setQ]               = useState('');
  const [cat, setCat]           = useState('All');
  const [sev, setSev]           = useState('All');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate]     = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const debounceRef = useRef(null);

  const fetch = useCallback(async (pg) => {
    if (!orgId) return;
    setLoading(true);
    try {
      const params = {
        page: pg ?? 1,
        limit: PAGE_SIZE,
        ...(cat !== 'All' ? { cat }   : {}),
        ...(sev !== 'All' ? { sev }   : {}),
        ...(fromDate ? { from: fromDate } : {}),
        ...(toDate   ? { to: toDate }     : {}),
      };
      const res  = await listOrgAudit(orgId, params);
      const rows = res?.data ?? res?.logs ?? (Array.isArray(res) ? res : []);
      setLogs(rows.map(a => ({ ...a, sev: (a.sev || 'info').toLowerCase() })));
      setTotal(res?.total ?? rows.length);
    } catch {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed to load audit logs' });
    } finally { setLoading(false); }
  }, [orgId, cat, sev, fromDate, toDate, toast]);

  useEffect(() => {
    setPage(1);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetch(1), 0);
    return () => clearTimeout(debounceRef.current);
  }, [fetch]);

  if (!org || runtimeLoading) return null;

  const canView   = perm.has('org.audit.view');
  const canExport = perm.has('org.audit.export');

  if (!canView) return (
    <div className="nx-cc-page">
      <DeniedState
        reason="insufficient_permission"
        message="Audit logs are restricted to Organization Admins and Auditors. Contact your org admin to request the Audit Log access capability."
      />
    </div>
  );

  const goPage = (pg) => { setPage(pg); fetch(pg); };
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const clearFilters = () => { setQ(''); setCat('All'); setSev('All'); setFromDate(''); setToDate(''); };
  const hasFilters   = q || cat !== 'All' || sev !== 'All' || fromDate || toDate;

  const displayedLogs = q.trim()
    ? logs.filter(a => {
        const term = q.trim().toLowerCase();
        return (
          (a.who    || '').toLowerCase().includes(term) ||
          (a.action || '').toLowerCase().includes(term) ||
          (a.target || '').toLowerCase().includes(term) ||
          (a.cat    || '').toLowerCase().includes(term)
        );
      })
    : logs;

  const exportCsv = () => {
    const rows = [
      ['Timestamp', 'Actor', 'Action', 'Target', 'Category', 'Severity'],
      ...displayedLogs.map(a => [fmtTs(a.createdAt), a.who || '', a.action || '', a.target || '', a.cat || '', fmtSev(a.sev)])
    ];
    const csv = rows.map(r => r.map(v => `"${String(v).replaceAll('"', '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const el   = document.createElement('a');
    const safeName = (org?.name ?? orgId).replace(/[^a-z0-9]/gi, '-').toLowerCase();
    el.href = url; el.download = `audit-${safeName}-${new Date().toISOString().slice(0, 10)}.csv`; el.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Audit Logs"
        sub={`Activity log for ${org.name} — ${total} event${total !== 1 ? 's' : ''}`}
        back={returnTo ? { label: returnLabel ? `Back to ${returnLabel}` : 'Back', onClick: () => navigate(returnTo) } : undefined}
        actions={canExport ? <Btn variant="ghost" icon="Download" onClick={exportCsv}>Export ({displayedLogs.length})</Btn> : null}
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <SearchInput value={q} onChange={e => setQ(e.target.value)} placeholder="Search actor, action, target, category…" width={280} />
        <select className="nx-input" style={{ width: 150, fontSize: 13 }} value={cat} onChange={e => setCat(e.target.value)}>
          {CATS.map(c => <option key={c}>{c}</option>)}
        </select>
        <select className="nx-input" style={{ width: 130, fontSize: 13 }} value={sev} onChange={e => setSev(e.target.value)}>
          {SEVS.map(s => (
            <option key={s} value={s}>
              {s === 'All' ? 'All Severity' : s.charAt(0).toUpperCase() + s.slice(1)}
            </option>
          ))}
        </select>
        <label htmlFor="audit-date-from" style={{ fontSize: 12, color: 'var(--nx-text-muted)', flexShrink: 0 }}>From</label>
        <input id="audit-date-from" type="date" className="nx-input" style={{ width: 140, fontSize: 13 }} value={fromDate} onChange={e => setFromDate(e.target.value)} />
        <label htmlFor="audit-date-to" style={{ fontSize: 12, color: 'var(--nx-text-muted)', flexShrink: 0 }}>To</label>
        <input id="audit-date-to" type="date" className="nx-input" style={{ width: 140, fontSize: 13 }} value={toDate}   onChange={e => setToDate(e.target.value)} />
        {hasFilters && <Btn variant="ghost" size="sm" icon="X" onClick={clearFilters}>Clear</Btn>}
      </div>

      {loading ? (
        <div className="nx-empty" style={{ padding: '32px 0' }}>
          <NexusIcon name="Loader" size={20} /><span>Loading audit logs…</span>
        </div>
      ) : displayedLogs.length === 0 ? (
        <div className="nx-empty" style={{ flexDirection: 'column', gap: 8 }}>
          <NexusIcon name="ClipboardList" size={22} />
          <span>No audit events found.</span>
          {hasFilters && (
            <Btn variant="ghost" size="sm" icon="X" onClick={clearFilters}>Clear filters</Btn>
          )}
        </div>
      ) : (
        <>
          <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
            <thead><tr>
              <Th icon="Clock">Timestamp</Th>
              <Th icon="User">Actor</Th>
              <Th icon="Activity">Action</Th>
              <Th icon="Target">Target</Th>
              <Th icon="Tag">Category</Th>
              <Th icon="AlertCircle">Severity</Th>
            </tr></thead>
            <tbody>{displayedLogs.map((a, i) => {
              const id  = a.id || String(i);
              const exp = expandedId === id;
              return (
                <React.Fragment key={id}>
                  <tr className="nx-tr" style={{ cursor: 'pointer' }} onClick={() => setExpandedId(exp ? null : id)}>
                    <td><span className="nx-muted nx-mini">{fmtTs(a.createdAt)}</span></td>
                    <td><span className="nx-muted nx-mini">{a.who || '—'}</span></td>
                    <td><span style={{ fontSize: 13, fontWeight: 500 }}>{a.action || '—'}</span></td>
                    <td><span className="nx-muted">{a.target || '—'}</span></td>
                    <td><Pill tone={CAT_TONE[a.cat] || 'gray'}>{a.cat || '—'}</Pill></td>
                    <td><Pill tone={SEV_TONE[a.sev] || 'gray'}>{fmtSev(a.sev)}</Pill></td>
                  </tr>
                  {exp && (
                    <tr key={id + '-detail'}>
                      <td colSpan={6} style={{ padding: '8px 14px 12px', background: 'var(--nx-surface-alt)' }}>
                        <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 6 }}>Event Details</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px', fontSize: 12, marginBottom: 6 }}>
                          {a.id         && <div><span style={{ color: 'var(--nx-text-muted)' }}>ID: </span>{a.id}</div>}
                          {a.time       && <div><span style={{ color: 'var(--nx-text-muted)' }}>Time: </span>{a.time}</div>}
                          {a.ip         && <div><span style={{ color: 'var(--nx-text-muted)' }}>IP: </span>{a.ip}</div>}
                          {a.browser    && <div><span style={{ color: 'var(--nx-text-muted)' }}>Browser: </span>{a.browser}</div>}
                          {a.os         && <div><span style={{ color: 'var(--nx-text-muted)' }}>OS: </span>{a.os}</div>}
                          {a.entityType && <div><span style={{ color: 'var(--nx-text-muted)' }}>Entity: </span>{a.entityType}</div>}
                          {a.entityId   && <div><span style={{ color: 'var(--nx-text-muted)' }}>Entity ID: </span>{a.entityId}</div>}
                          {a.correlationId && <div><span style={{ color: 'var(--nx-text-muted)' }}>Correlation: </span>{a.correlationId}</div>}
                        </div>
                        <DiffBlock before={a.entityBefore} after={a.entityAfter} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}</tbody>
          </table></div></div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12, alignItems: 'center' }}>
              <Btn variant="ghost" size="sm" icon="ChevronLeft" disabled={page <= 1} onClick={() => goPage(page - 1)}>Prev</Btn>
              <span style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>Page {page} of {totalPages}</span>
              <Btn variant="ghost" size="sm" icon="ChevronRight" disabled={page >= totalPages} onClick={() => goPage(page + 1)}>Next</Btn>
            </div>
          )}
        </>
      )}
    </div>
  );
}
